import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Form, Button, Flex, Select, Input, Spin, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import LocationSelector from "../../components/LocationSelector";
import {
  useFetchCleaningItemsQuery,
  useCreateCleaningLogMutation,
} from "./cleaning.service";
import "../incident/style.css";

export default function CleaningLogCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user);
  const [locationId, setLocationId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    setLocationId(user?.activeLocationId || user?.restaurantId);
  }, [user]);

  const { data = { data: { items: [] } }, isLoading: itemsLoading } =
    useFetchCleaningItemsQuery(
      { locationId },
      { skip: !locationId, refetchOnMountOrArgChange: true }
    );

  const items = data?.data?.items || [];
  const [createLog, { isLoading: saving }] = useCreateCleaningLogMutation();

  const onFinish = async (values) => {
    if (!locationId) {
      message.error(t("Please select a location first"));
      return;
    }
    try {
      await createLog({
        itemId: values.itemId,
        notes: values.notes,
        photo: photoFile,
        locationId,
      }).unwrap();
      message.success(t("Cleaning record saved"));
      navigate("/cleaning");
    } catch (e) {
      message.error(e?.data?.message || t("Failed to save cleaning record"));
    }
  };

  return (
    <Spin spinning={itemsLoading}>
      <Card
        className="incident-page"
        bordered={false}
        title={
          <Flex align="center" gap="middle" wrap>
            <span>{t("Add cleaning record")}</span>
            <LocationSelector onLocationChange={setLocationId} />
          </Flex>
        }
        extra={<Button onClick={() => navigate("/cleaning")}>{t("BACK")}</Button>}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label={t("Item cleaned")}
            name="itemId"
            rules={[{ required: true, message: t("Please select an item") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t("Search item cleaned")}
              disabled={!locationId || items.length === 0}
              options={items.map((item) => ({
                value: item._id,
                label: item.name,
              }))}
            />
          </Form.Item>

          <Form.Item label={t("Cleaned by")}>
            <Input disabled value={user?.name || user?.email || ""} />
          </Form.Item>

          <Form.Item label={t("Date & time")}>
            <Input disabled value={new Date().toLocaleString()} />
          </Form.Item>

          <Form.Item label={t("Notes")} name="notes">
            <Input.TextArea rows={3} placeholder={t("Optional")} />
          </Form.Item>

          <Form.Item label={t("Photo (optional)")}>
            <Upload
              beforeUpload={(file) => {
                setPhotoFile(file);
                return false;
              }}
              onRemove={() => setPhotoFile(null)}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>{t("Upload photo")}</Button>
            </Upload>
          </Form.Item>

          <Flex justify="center">
            <Button type="primary" htmlType="submit" loading={saving} disabled={!locationId}>
              {t("Submit")}
            </Button>
          </Flex>
        </Form>
      </Card>
    </Spin>
  );
}
